// ============================================
// Platform detection + native API wrappers
// Provides isNative() check and lazy-loaded
// native plugin imports for iOS/Android.
// ============================================

import { Capacitor } from '@capacitor/core';

/** True when running inside the native iOS/Android shell */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/** Get the current platform: 'ios', 'android', or 'web' */
export function getPlatform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

/**
 * Trigger a haptic impact. Falls back to no-op on web.
 * Style: 'light' for taps, 'medium' for confirmations, 'heavy' for errors.
 */
export async function hapticImpact(style: 'light' | 'medium' | 'heavy' = 'medium'): Promise<void> {
  if (!isNative()) return;
  const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
  const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
  await Haptics.impact({ style: map[style] });
}

/** Trigger a haptic notification. Falls back to no-op on web. */
export async function hapticNotification(type: 'success' | 'warning' | 'error' = 'success'): Promise<void> {
  if (!isNative()) return;
  const { Haptics, NotificationType } = await import('@capacitor/haptics');
  const map = { success: NotificationType.Success, warning: NotificationType.Warning, error: NotificationType.Error };
  await Haptics.notification({ type: map[type] });
}

/** Configure status bar for the current theme */
export async function configureStatusBar(isDark: boolean): Promise<void> {
  if (!isNative()) return;
  const { StatusBar, Style } = await import('@capacitor/status-bar');
  // Style.Light = dark text on light background (for our white app theme)
  // Style.Dark = light text on dark background (for nocturnal-pulse theme)
  await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
  // Let the app background fill behind the status bar for a seamless look.
  // All pages use env(safe-area-inset-top) for content padding so nothing
  // gets clipped under the notch/Dynamic Island.
  await StatusBar.setOverlaysWebView({ overlay: true });
}

/**
 * Open an external URL. On native iOS, routes through Safari View Controller
 * via @capacitor/browser so the user stays within an in-app browser chrome
 * (back to app button, preserves session) instead of leaving the app for
 * Safari or getting trapped inside the WKWebView. On web, opens a new tab.
 *
 * Use this for any outbound link (PubMed, YouTube, Apple URLs, etc.).
 * Apple Guideline 4.2 flags apps that trap users in their webview.
 */
export async function openExternal(url: string): Promise<void> {
  if (!isNative()) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url, presentationStyle: 'popover' });
  } catch (err) {
    console.warn('[openExternal] Browser plugin failed, falling back to window.open', err);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Triggers the platform's native share sheet.
 *
 * - iOS: uses @capacitor/share, which presents Apple's share sheet
 *   (iMessage, Mail, WhatsApp, Copy, etc.). Returns when the user
 *   either picks a target or cancels — both resolve as success here
 *   since cancellation is not an error from the caller's POV.
 * - Web: uses navigator.share() if available (mobile Safari, Chrome
 *   on Android, recent Chrome desktop). Otherwise falls back to
 *   copying the URL to the clipboard so the user has SOMETHING to
 *   paste manually.
 *
 * Returns:
 *  - 'shared' when the share sheet was presented
 *  - 'copied' when we fell back to clipboard
 *  - 'unsupported' when neither path worked (very old browsers)
 */
export async function shareNative(input: {
  title?: string;
  text?: string;
  url: string;
}): Promise<'shared' | 'copied' | 'unsupported'> {
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({
        title: input.title,
        text: input.text,
        url: input.url,
        dialogTitle: input.title ?? 'Share',
      });
      return 'shared';
    } catch (err) {
      // Capacitor's Share plugin throws on user-cancel too; treat as
      // success since the user chose not to share, not an error.
      const message = err instanceof Error ? err.message : String(err);
      if (/cancel/i.test(message)) return 'shared';
      console.warn('[shareNative] Capacitor share failed, falling back', err);
    }
  }

  // Web path: navigator.share if available.
  const navAny = typeof navigator !== 'undefined' ? (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }) : undefined;
  if (navAny && typeof navAny.share === 'function') {
    try {
      await navAny.share({
        title: input.title,
        text: input.text,
        url: input.url,
      });
      return 'shared';
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (/abort|cancel/i.test(message)) return 'shared';
      console.warn('[shareNative] navigator.share failed, falling back', err);
    }
  }

  // Clipboard fallback.
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(input.url);
      return 'copied';
    }
  } catch (err) {
    console.warn('[shareNative] clipboard write failed', err);
  }

  return 'unsupported';
}

/**
 * Export a text file on native iOS/Android.
 *
 * The web export path (blob URL + <a download>) is silently ignored by
 * the iOS WKWebView - the `download` attribute isn't supported - so on
 * device the Export button used to do nothing. Here we write the file to
 * the Cache directory and hand its URI to the native share sheet, giving
 * the user Save to Files / Mail / AirDrop. Caller keeps the blob-download
 * path for web (see ProfilePage.handleExport).
 *
 * Returns 'shared' when the sheet was presented (or the user cancelled -
 * cancellation is not an error), 'failed' otherwise.
 */
export async function exportFileNative(input: {
  filename: string;
  data: string;
}): Promise<'shared' | 'failed'> {
  try {
    const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
    await Filesystem.writeFile({
      path: input.filename,
      data: input.data,
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });
    const { uri } = await Filesystem.getUri({
      path: input.filename,
      directory: Directory.Cache,
    });
    const { Share } = await import('@capacitor/share');
    await Share.share({
      title: 'PRTCL data export',
      url: uri,
      dialogTitle: 'Export your PRTCL data',
    });
    return 'shared';
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // The Share plugin throws on user-cancel; that's a success from the
    // caller's POV (the file was written, the user just chose not to send).
    if (/abort|cancel/i.test(message)) return 'shared';
    console.warn('[exportFileNative] failed', err);
    return 'failed';
  }
}

/** Listen for deep link opens (universal links / app URL opens) */
export function onAppUrlOpen(callback: (url: string) => void): void {
  if (!isNative()) return;
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appUrlOpen', (event) => {
      callback(event.url);
    });
  });
}

/**
 * Fire when the app returns to the FOREGROUND (backgrounded -> active).
 * The scheduled-protocol reconcile hangs off this: a backgrounded app
 * crossing local midnight (or a start date) catches up the moment the
 * user comes back - no server cron. No-op on web.
 */
export function onAppForeground(callback: () => void): void {
  if (!isNative()) return;
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) callback();
    });
  });
}

/**
 * Action type identifiers used on iOS notifications. Registered once at
 * app startup via registerNotificationActionTypes(). Notifications
 * include the matching actionTypeId so iOS shows the right buttons
 * when the notification expands.
 */
export const NOTIFICATION_ACTION_TYPE_DOSE = 'DOSE_REMINDER';
export const NOTIFICATION_ACTION_LOG_NOW = 'LOG_NOW';
export const NOTIFICATION_ACTION_SKIP = 'SKIP';

/**
 * Register notification action types with iOS. Idempotent - safe to
 * call multiple times. Must be called BEFORE any notification with
 * actionTypeId is scheduled, otherwise iOS doesn't know about the
 * actions and shows the default tap-to-open behavior only.
 */
export async function registerNotificationActionTypes(): Promise<void> {
  if (!isNative()) return;
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  await LocalNotifications.registerActionTypes({
    types: [
      {
        id: NOTIFICATION_ACTION_TYPE_DOSE,
        actions: [
          { id: NOTIFICATION_ACTION_LOG_NOW, title: 'Log Now', foreground: true },
          // Skip is foreground:false — iOS dismisses the notification
          // and records the action without bringing the app forward.
          // The actual skip is performed in the notification-action
          // listener via skipDoseFromNotification(), which works
          // without React.
          { id: NOTIFICATION_ACTION_SKIP, title: 'Skip', foreground: false, destructive: true },
        ],
      },
    ],
  });
}

/**
 * Fire a native local notification immediately.
 * On web, this is a no-op (web uses browser Notification API separately).
 */
export async function fireNativeNotification(opts: {
  id: number;
  title: string;
  body: string;
  tag: string;
  data?: Record<string, string>;
  actionTypeId?: string;
}): Promise<void> {
  if (!isNative()) return;
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  await LocalNotifications.schedule({
    notifications: [
      {
        id: opts.id,
        title: opts.title,
        body: opts.body,
        extra: opts.data,
        actionTypeId: opts.actionTypeId,
        iconColor: '#14808A',
      },
    ],
  });
}

/**
 * Pre-schedule a batch of native local notifications for future delivery.
 * iOS holds these schedules independently of the app's JS runtime, so
 * they fire at the exact second even when the app is killed, the phone
 * is locked, or the user is in another app.
 *
 * Each notification needs a unique numeric `id`. Re-scheduling with the
 * same id replaces the existing schedule (per Capacitor docs).
 */
export async function scheduleNativeNotifications(
  notifications: Array<{
    id: number;
    title: string;
    body: string;
    at: Date;
    data?: Record<string, string>;
    actionTypeId?: string;
  }>,
): Promise<void> {
  if (!isNative()) return;
  if (notifications.length === 0) return;
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  await LocalNotifications.schedule({
    notifications: notifications.map((n) => ({
      id: n.id,
      title: n.title,
      body: n.body,
      schedule: { at: n.at, allowWhileIdle: true },
      extra: n.data,
      actionTypeId: n.actionTypeId,
      iconColor: '#14808A',
    })),
  });
}

/**
 * Cancel one or more pending scheduled notifications by id. Safe to
 * call with ids that don't exist - iOS no-ops.
 */
export async function cancelNativeNotifications(ids: number[]): Promise<void> {
  if (!isNative()) return;
  if (ids.length === 0) return;
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  await LocalNotifications.cancel({ notifications: ids.map((id) => ({ id })) });
}

/**
 * List the IDs of all currently-pending scheduled notifications. Used
 * by the reminder sync to figure out what to cancel before scheduling
 * a fresh batch.
 */
export async function listPendingNativeNotifications(): Promise<Array<{ id: number }>> {
  if (!isNative()) return [];
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  const result = await LocalNotifications.getPending();
  return result.notifications.map((n) => ({ id: n.id }));
}

/**
 * Like listPendingNativeNotifications but returns full notification
 * data — title, body, scheduled fire time, extras. Used by the
 * /debug/notifications diagnostic page; not used by the reminder
 * engine (which only needs ids to cancel).
 */
export async function listPendingNativeNotificationsDetailed(): Promise<Array<{
  id: number;
  title: string;
  body: string;
  at: string | null;
  extra: Record<string, unknown> | null;
  actionTypeId?: string;
}>> {
  if (!isNative()) return [];
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  const result = await LocalNotifications.getPending();
  return result.notifications.map((n: any) => ({
    id: n.id,
    title: n.title,
    body: n.body,
    at: n.schedule?.at ? new Date(n.schedule.at).toISOString() : null,
    extra: n.extra ?? null,
    actionTypeId: n.actionTypeId,
  }));
}

/**
 * Request notification permission (native or web).
 */
export async function requestNativeNotificationPermission(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!isNative()) {
    if (typeof Notification === 'undefined') return 'unsupported';
    const result = await Notification.requestPermission();
    return result === 'granted' ? 'granted' : 'denied';
  }
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  const perm = await LocalNotifications.requestPermissions();
  return perm.display === 'granted' ? 'granted' : 'denied';
}

/**
 * Check notification permission status (native or web).
 */
export async function checkNativeNotificationPermission(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!isNative()) {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission === 'granted' ? 'granted' : 'denied';
  }
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  const perm = await LocalNotifications.checkPermissions();
  return perm.display === 'granted' ? 'granted' : 'denied';
}
